/**
 * useStudentSessionWS — opens a WebSocket to /api/ws/student-session and
 * dispatches a `pm-auth-event` (session_replaced / forced_logout) the instant
 * the server pushes it. Falls back gracefully — if the WS can't connect, the
 * 30-second poll in AuthContext still catches the event eventually.
 *
 * Behaviors:
 *   - Connects only for students with a token
 *   - Reconnects with exponential backoff (1s, 2s, 4s, 8s, max 30s)
 *   - Disconnects cleanly on logout / unmount
 *   - Sends "ping" every 25s to keep proxies (Cloudflare, Nginx) from idling out
 *   - Close code 4401 = auth-related — does NOT reconnect (would just fail again)
 */
import { useEffect, useRef } from "react";
import { BACKEND_URL } from "@/lib/api";

function dispatch(kind) {
  try { window.dispatchEvent(new CustomEvent("pm-auth-event", { detail: { kind } })); } catch { /* noop */ }
}

function dispatchEnrollment(detail) {
  try { window.dispatchEvent(new CustomEvent("pm-enrollment-suspended", { detail })); } catch { /* noop */ }
}

export function useStudentSessionWS(active) {
  const wsRef = useRef(null);
  const pingRef = useRef(null);
  const backoffRef = useRef(1000);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    stoppedRef.current = false;
    backoffRef.current = 1000;

    const buildUrl = () => {
      const token = localStorage.getItem("pm_token");
      if (!token || !BACKEND_URL) return null;
      const wsBase = BACKEND_URL.replace(/^http/i, "ws");
      return `${wsBase}/api/ws/student-session?token=${encodeURIComponent(token)}`;
    };

    const open = () => {
      if (stoppedRef.current) return;
      const url = buildUrl();
      if (!url) return;
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = 1000; // reset backoff on success
        if (pingRef.current) clearInterval(pingRef.current);
        pingRef.current = setInterval(() => {
          try { if (ws.readyState === 1) ws.send("ping"); } catch { /* noop */ }
        }, 25000);
      };

      ws.onmessage = (e) => {
        let msg;
        try { msg = JSON.parse(e.data); } catch { return; }
        if (!msg?.type) return;
        if (msg.type === "session_replaced" || msg.type === "forced_logout") {
          stoppedRef.current = true; // don't reconnect — we're being kicked out
          try { ws.close(); } catch { /* noop */ }
          dispatch("session_replaced");
        } else if (msg.type === "enrollment_changed") {
          // Admin revoked or suspended a course. Bubble the payload up so the
          // global SuspendedAccessDialog opens and (if the student is currently
          // inside that course) navigates them out.
          dispatchEnrollment({
            code: msg.code || "enrollment_suspended",
            course_id: msg.course_id,
            course_name: msg.course_name || "",
            reason: msg.reason || "",
          });
        }
        // "hello", "ping", "pong" are ignored
      };

      ws.onerror = () => { /* onclose will follow */ };

      ws.onclose = (e) => {
        if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
        wsRef.current = null;
        // 4401/4403 → auth issue; bubble up as session_replaced (the poll will confirm)
        if (e.code === 4401 || e.code === 4403) {
          stoppedRef.current = true;
          if (e.reason === "session_replaced") dispatch("session_replaced");
          return;
        }
        scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      if (stoppedRef.current) return;
      const delay = backoffRef.current;
      backoffRef.current = Math.min(backoffRef.current * 2, 30000);
      setTimeout(() => { if (!stoppedRef.current) open(); }, delay);
    };

    open();

    return () => {
      stoppedRef.current = true;
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } wsRef.current = null; }
    };
  }, [active]);
}
